"use client";

import React, { useRef, useEffect, useState, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Line, Sphere, Box, Html } from '@react-three/drei';
import * as THREE from 'three';
import { useCubeFrameloop, CUBE_DPR } from './useCubeFrameloop';
import { useWebGLSupport } from './useWebGLSupport';
import CubeStaticFallback from './CubeStaticFallback';

// 3D cube visualization for group quiz comparisons. Shares the visual chrome
// (grid, axis lines, macro-cell tinting, axis labels) with ResultCube but
// renders multiple labeled member points instead of a single user point.
//
// Kept as a separate component rather than generalizing ResultCube to avoid
// touching the well-tested single-point cube. The chrome is duplicated here
// because the per-component state (auto-rotate flag, label visibility) is
// captured in closures that don't extract cleanly.

export interface GroupCubePoint {
  userId: string;
  label: string;
  x: number;
  y: number;
  z: number;
  color: string;
  isYou?: boolean;
}

interface GroupCubeProps {
  points: GroupCubePoint[];
  centroid?: { x: number; y: number; z: number } | null;
  showCentroid?: boolean;
  // When true, draws polyline segments connecting consecutive points in
  // the input array. Used by the past-self comparison page to show
  // movement over time.
  connectPoints?: boolean;
}

const MACRO_CELL_COLORS = {
  topLeft: '#ff9ea0',
  topCenter: '#ff9fff',
  topRight: '#9f9fff',
  middleLeft: '#ffcfa1',
  middleCenter: '#e5e5e5',
  middleRight: '#9ffffe',
  bottomLeft: '#9fff9e',
  bottomCenter: '#d4fe9a',
  bottomRight: '#ffff9f',
};

function ColoredMacroCells() {
  // For groups, no single user cell to highlight; render uniform low opacity.
  const cellSize = 66.67;
  const positions = [
    { pos: [-66.67, 66.67, 0], color: MACRO_CELL_COLORS.topLeft, key: 'topLeft' },
    { pos: [0, 66.67, 0], color: MACRO_CELL_COLORS.topCenter, key: 'topCenter' },
    { pos: [66.67, 66.67, 0], color: MACRO_CELL_COLORS.topRight, key: 'topRight' },
    { pos: [-66.67, 0, 0], color: MACRO_CELL_COLORS.middleLeft, key: 'middleLeft' },
    { pos: [0, 0, 0], color: MACRO_CELL_COLORS.middleCenter, key: 'middleCenter' },
    { pos: [66.67, 0, 0], color: MACRO_CELL_COLORS.middleRight, key: 'middleRight' },
    { pos: [-66.67, -66.67, 0], color: MACRO_CELL_COLORS.bottomLeft, key: 'bottomLeft' },
    { pos: [0, -66.67, 0], color: MACRO_CELL_COLORS.bottomCenter, key: 'bottomCenter' },
    { pos: [66.67, -66.67, 0], color: MACRO_CELL_COLORS.bottomRight, key: 'bottomRight' },
  ];
  return (
    <>
      {positions.map(({ pos, color, key }) => (
        <Box key={key} args={[cellSize, cellSize, 200]} position={pos as [number, number, number]}>
          {/* 0.35 to match ResultCube's non-highlighted macro-cell opacity,
              so the group cube reads identically to the hero cube. */}
          <meshBasicMaterial color={color} transparent opacity={0.35} />
        </Box>
      ))}
    </>
  );
}

function GridLines() {
  const lines: React.ReactNode[] = [];
  const gridSize = 3;
  const step = 200 / gridSize;
  for (let i = 0; i <= gridSize; i++) {
    const pos = -100 + i * step;
    lines.push(
      <Line key={`yz-h-l-${i}`} points={[[-100, pos, -100], [-100, pos, 100]]} color="#e5e7eb" lineWidth={1} />,
      <Line key={`yz-h-r-${i}`} points={[[100, pos, -100], [100, pos, 100]]} color="#e5e7eb" lineWidth={1} />,
      <Line key={`yz-v-l-${i}`} points={[[-100, -100, pos], [-100, 100, pos]]} color="#e5e7eb" lineWidth={1} />,
      <Line key={`yz-v-r-${i}`} points={[[100, -100, pos], [100, 100, pos]]} color="#e5e7eb" lineWidth={1} />,
      <Line key={`xz-h-b-${i}`} points={[[pos, -100, -100], [pos, -100, 100]]} color="#e5e7eb" lineWidth={1} />,
      <Line key={`xz-h-t-${i}`} points={[[pos, 100, -100], [pos, 100, 100]]} color="#e5e7eb" lineWidth={1} />,
      <Line key={`xy-v-b-${i}`} points={[[-100, pos, -100], [100, pos, -100]]} color="#e5e7eb" lineWidth={1} />,
      <Line key={`xy-v-f-${i}`} points={[[-100, pos, 100], [100, pos, 100]]} color="#e5e7eb" lineWidth={1} />,
    );
  }
  return <>{lines}</>;
}

function AxisLines() {
  return (
    <>
      <Line points={[[-100, 0, 0], [100, 0, 0]]} color="#374151" lineWidth={4} />
      <Line points={[[0, -100, 0], [0, 100, 0]]} color="#374151" lineWidth={4} />
      <Line points={[[0, 0, -100], [0, 0, 100]]} color="#374151" lineWidth={4} />
    </>
  );
}

function CubeFrame() {
  const vertices: [number, number, number][] = [
    [-100, -100, -100], [100, -100, -100], [100, 100, -100], [-100, 100, -100],
    [-100, -100, 100], [100, -100, 100], [100, 100, 100], [-100, 100, 100],
  ];
  const edges: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [4, 5], [5, 6], [6, 7], [7, 4],
    [0, 4], [1, 5], [2, 6], [3, 7],
  ];
  return (
    <>
      {edges.map((edge, i) => (
        <Line
          key={i}
          points={[vertices[edge[0]], vertices[edge[1]]]}
          color="#9ca3af"
          lineWidth={1}
        />
      ))}
    </>
  );
}

function MovingLabels() {
  const { camera } = useThree();
  const [v, setV] = useState({
    showEconLeft: true,
    showEconRight: false,
    showLib: true,
    showAuth: false,
    showProg: true,
    showCons: false,
  });
  useFrame(() => {
    const cp = camera.position;
    const dEL = cp.distanceTo(new THREE.Vector3(-120, 0, 0));
    const dER = cp.distanceTo(new THREE.Vector3(120, 0, 0));
    const dLib = cp.distanceTo(new THREE.Vector3(0, -120, 0));
    const dAuth = cp.distanceTo(new THREE.Vector3(0, 120, 0));
    const dProg = cp.distanceTo(new THREE.Vector3(0, 0, -120));
    const dCons = cp.distanceTo(new THREE.Vector3(0, 0, 120));
    setV({
      showEconLeft: dEL < dER,
      showEconRight: dEL >= dER,
      showLib: dLib < dAuth,
      showAuth: dLib >= dAuth,
      showProg: dProg < dCons,
      showCons: dProg >= dCons,
    });
  });
  const cls = "text-xs font-medium text-slate-700 bg-white/90 px-2 py-1 rounded-sm pointer-events-none whitespace-nowrap select-none";
  return (
    <>
      {v.showEconLeft && <Html position={[-120, 0, 0]} center><div className={cls}>Economic Left</div></Html>}
      {v.showEconRight && <Html position={[120, 0, 0]} center><div className={cls}>Economic Right</div></Html>}
      {v.showLib && <Html position={[0, -120, 0]} center><div className={cls}>Libertarian</div></Html>}
      {v.showAuth && <Html position={[0, 120, 0]} center><div className={cls}>Authoritarian</div></Html>}
      {v.showProg && <Html position={[0, 0, -120]} center><div className={cls}>Progressive</div></Html>}
      {v.showCons && <Html position={[0, 0, 120]} center><div className={cls}>Conservative</div></Html>}
    </>
  );
}

function MemberPoint({ point }: { point: GroupCubePoint }) {
  const meshRef = useRef<THREE.Mesh>(null);
  // "You" pulses; others are static for visual contrast.
  useFrame((state) => {
    if (meshRef.current && point.isYou) {
      meshRef.current.scale.setScalar(1 + Math.sin(state.clock.elapsedTime * 2) * 0.1);
    }
  });
  // Match ResultCube's UserPoint: clean emissive sphere (radius 6), "you" a
  // touch bigger and brighter.
  const radius = point.isYou ? 7 : 6;
  return (
    <>
      <Sphere ref={meshRef} args={[radius, 32, 32]} position={[point.x, point.y, point.z]}>
        <meshStandardMaterial
          color={point.color}
          emissive={point.color}
          emissiveIntensity={point.isYou ? 0.6 : 0.5}
          metalness={0.3}
          roughness={0.1}
        />
      </Sphere>
      {/* Small, light label — not the old chunky bordered puck. */}
      <Html position={[point.x, point.y + 12, point.z]} center distanceFactor={260}>
        <div
          className={`pointer-events-none select-none whitespace-nowrap rounded px-1 py-px text-[10px] font-semibold shadow-sm ${point.isYou ? 'bg-[#2a4173] text-white' : 'bg-white/85 text-slate-700'}`}
          style={{ transform: 'translate(-50%, -50%)' }}
        >
          {point.label}
        </div>
      </Html>
    </>
  );
}

function CentroidPoint({ x, y, z }: { x: number; y: number; z: number }) {
  return (
    <>
      <Sphere args={[8, 32, 32]} position={[x, y, z]}>
        <meshStandardMaterial color="#94a3b8" transparent opacity={0.4} />
      </Sphere>
      <Html position={[x, y - 14, z]} center distanceFactor={250}>
        <div
          className="text-xs font-medium px-2 py-0.5 rounded-md bg-gray-700/80 text-white pointer-events-none whitespace-nowrap select-none"
          style={{ transform: 'translate(-50%, -50%)' }}
        >
          group center
        </div>
      </Html>
    </>
  );
}

type OrbitControlsHandle = React.ComponentRef<typeof OrbitControls>;

function ConnectingLines({ points }: { points: GroupCubePoint[] }) {
  if (points.length < 2) return null;
  // Render one Line segment between each consecutive pair. Color matches
  // the second endpoint's color for a "moved into this position" feel.
  const segments: React.ReactNode[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    segments.push(
      <Line
        key={`seg-${a.userId}-${b.userId}`}
        points={[[a.x, a.y, a.z], [b.x, b.y, b.z]]}
        color={b.color}
        lineWidth={3}
        opacity={0.7}
        transparent
      />,
    );
  }
  return <>{segments}</>;
}

function CubeContent({ points, centroid, showCentroid, connectPoints }: GroupCubeProps) {
  const controlsRef = useRef<OrbitControlsHandle | null>(null);
  const autoRotateRef = useRef(true);
  const { gl } = useThree();

  useEffect(() => {
    // Respect prefers-reduced-motion: never auto-rotate for users who opt out
    // of vestibular-triggering animations. Re-check on the media query so a
    // user toggling the OS setting mid-session takes effect.
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      const apply = () => { if (mq.matches) autoRotateRef.current = false; };
      apply();
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
  }, []);

  useEffect(() => {
    const stop = () => { autoRotateRef.current = false; };
    gl.domElement.addEventListener('mousedown', stop);
    gl.domElement.addEventListener('touchstart', stop);
    return () => {
      gl.domElement.removeEventListener('mousedown', stop);
      gl.domElement.removeEventListener('touchstart', stop);
    };
  }, [gl.domElement]);

  useFrame(() => {
    if (controlsRef.current) controlsRef.current.autoRotate = autoRotateRef.current;
  });

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[150, 150, 150]} intensity={0.5} />
      <directionalLight position={[-150, -150, -150]} intensity={0.3} />
      <ColoredMacroCells />
      <GridLines />
      <CubeFrame />
      <AxisLines />
      <MovingLabels />
      {showCentroid && centroid && <CentroidPoint x={centroid.x} y={centroid.y} z={centroid.z} />}
      {connectPoints && <ConnectingLines points={points} />}
      {points.map((p) => (
        <MemberPoint key={p.userId} point={p} />
      ))}
      <OrbitControls
        ref={controlsRef}
        enablePan={false}
        minDistance={320}
        maxDistance={800}
        target={[0, 0, 0]}
        enableDamping
        dampingFactor={0.08}
        // eslint-disable-next-line react-hooks/refs -- mirror of ResultCube pattern; one-time init seed
        autoRotate={autoRotateRef.current}
        autoRotateSpeed={1.2}
        enableZoom
      />
    </>
  );
}

export default function GroupCube({ points, centroid, showCentroid = true, connectPoints = false }: GroupCubeProps) {
  // Build a screen-reader summary of where every member lands. Three.js
  // canvases are otherwise opaque to assistive tech, so this text label is
  // the only path to the underlying data.
  const ariaLabel = points.length === 0
    ? '3D political compass cube. No member positions yet.'
    : `3D political compass cube showing ${points.length} ${points.length === 1 ? 'position' : 'positions'}: ${points
        .map((p) => `${p.label}${p.isYou ? ' (you)' : ''} at economic ${Math.round(p.x)}, governance ${Math.round(p.y)}, social ${Math.round(p.z)}`)
        .join('; ')}.`;
  const containerRef = useRef<HTMLDivElement>(null);
  // Pause WebGL rendering when the cube is off-screen (the landing + group
  // reveal cubes are well below the fold) and render-on-demand for reduced
  // motion, so it stops eating main-thread time and inflating INP.
  const frameloop = useCubeFrameloop(containerRef);
  // No WebGL2 (context creation would throw outside React and leave a dead
  // blank box): show the members on a flat projection instead. The
  // aria-label above still carries every position for assistive tech.
  const webgl = useWebGLSupport();
  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-square bg-gray-50 rounded-lg select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
      role="img"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      {webgl ? (
        <Canvas
          frameloop={frameloop}
          dpr={CUBE_DPR}
          camera={{ position: [400, 350, 400], fov: 35, near: 10, far: 1200 }}
          style={{ background: 'transparent' }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: 'low-power',
            preserveDrawingBuffer: true,
            failIfMajorPerformanceCaveat: false,
          }}
        >
          <Suspense fallback={null}>
            <CubeContent
              points={points}
              centroid={centroid}
              showCentroid={showCentroid}
              connectPoints={connectPoints}
            />
          </Suspense>
        </Canvas>
      ) : (
        <CubeStaticFallback points={points} />
      )}
    </div>
  );
}
